import compact from 'lodash/compact';
import get from 'lodash/get';
import isFinite from 'lodash/isFinite';
import pickBy from 'lodash/pickBy';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { push } from 'react-router-redux';
import { all, call, delay, put, select, take } from 'redux-saga/effects';

import {
  addRecentlyPlayedPodcasts,
  resetFilterOffsets,
  setFetchingOffsets,
  setHasMetRecentlyPlayedThreshold,
} from '../actions';
import {
  ANNOTATION_LIMIT,
  CollectionSorts,
  DOMAIN_NAME,
  FilterTypes,
  GET_SORTED_BY_TYPES_ALL,
  GET_SORTED_BY_TYPES_ALL_PREMIUM,
  INITIAL_LOAD_LIMIT,
  RECENTLY_PLAYED_PLAYBACK_TIMER_THRESHOLD,
  SORTED_ITEMS_LIMIT,
} from '../constants';
import * as D from '../deps';
import * as CollectionServices from '../services';
import {
  getCollectionPreviousVersion,
  getCollectionVersion,
  getCurrentFilter,
  getFilterCurrentViewedOffset,
  getFilteredCollectionTotalCount,
  getFilterSort,
  getIsCurrentlySyncing,
  getRecentlyPlayedSPSItems,
} from '../utils';

const LOOP = true;

const spawn = D.taskManagerSpawnFactory(DOMAIN_NAME);

export function* onDomainLoad(): $TSFixMe {
  yield all([
    spawn(pollCollectionVersion), // start up the polling mechanism when we load the collection domain
  ]);
}

export function* getItems({ cursor, limit, sinceVersion }: $TSFixMe): $TSFixMe {
  const collectionV7EndpointsEnabled = yield select(D.Utils.TunerVars.collectionV7EndpointsEnabled);
  yield call(CollectionServices.getItems, {
    collectionV7EndpointsEnabled,
    cursor,
    limit,
    sinceVersion,
  });
}

export function* initialize(): $TSFixMe {
  const playlistSort = yield select(getFilterSort, FilterTypes.PLAYLISTS);
  yield all([
    spawn(CollectionServices.getSortedPlaylists, {
      allowedTypes: D.Constants.SourceTypes.PlaylistSupportedTypes,
      annotationLimit: ANNOTATION_LIMIT,
      limit: INITIAL_LOAD_LIMIT,
      sortOrder: playlistSort,
    }),
    spawn(getItems, { limit: INITIAL_LOAD_LIMIT }),
  ]);
}

export function* onGetItemsSuccess(action: $TSFixMe): $TSFixMe {
  const { cursor } = action.response;
  // Are there more to load?
  if (cursor) {
    yield call(getItems, { limit: INITIAL_LOAD_LIMIT, cursor });
  }
  // Polling will eventually check to see if we need to do a partial sync,
  // which is handled in onGetCollectionVersionSuccess
}

export function* onAddItemSuccess(action: $TSFixMe): $TSFixMe {
  const previousVersion = yield select(getCollectionPreviousVersion);
  if (action.response.previousVersion !== previousVersion) {
    yield call(getItems, { sinceVersion: previousVersion });
  }

  const seenWebPremiumWelcome = yield select(D.hasSeenWebPremiumWelcome);
  if (!seenWebPremiumWelcome) {
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ seenWebPremiumWelcome: true; }... Remove this comment to see the full error message
    yield put(D.ApiUpdateSettings({ seenWebPremiumWelcome: true }));
  }

  const idAdded = get(action.params, 'content.request.pandoraId');
  const typeAdded = D.getPandoraTypeFromPandoraId(idAdded);
  const message = (
    <FormattedMessage {...D.messages.itemAddedToCollection} values={{ pandoraType: typeAdded }} />
  );
  yield put(D.openModalCoachmark({ bodyMessage: message }));
}

export function* onRemoveItemSuccess(action: $TSFixMe): $TSFixMe {
  const previousVersion = yield select(getCollectionPreviousVersion);
  if (action.response.previousVersion !== previousVersion) {
    yield call(getItems, { sinceVersion: previousVersion });
  }
  const idRemoved = get(action.params, 'content.request.pandoraId');
  const typeRemoved = D.getPandoraTypeFromPandoraId(idRemoved);
  const message = (
    <FormattedMessage
      {...D.messages.itemRemovedFromCollection}
      values={{ pandoraType: typeRemoved }}
    />
  );
  yield put(D.openModalCoachmark({ bodyMessage: message }));

  const currentSourceId = yield select(
    D.getInDomain('playback.selectors.getCurrentItemSourceId', ''),
  );
  if (currentSourceId) {
    const currentType = D.Utils.SourceType.getPandoraTypeFromPandoraId(currentSourceId);
    const isPlayingCT = currentType === D.Constants.SourceTypes.PandoraTypes.COLLECTED_TRACKS;
    let isPlayingAssociatedCA;
    if (
      currentType === D.Constants.SourceTypes.PandoraTypes.COLLECTED_ALBUM_TRACKS &&
      typeRemoved === D.Constants.SourceTypes.PandoraTypes.SONG
    ) {
      const annotations = yield select(D.getAnnotationsById, idRemoved);
      isPlayingAssociatedCA =
        annotations &&
        currentSourceId ===
          D.Utils.SourceType.getCollectedAlbumTracksId(annotations.get('albumId'));
    }

    if (isPlayingCT || isPlayingAssociatedCA) {
      const currentPandoraId = yield select(
        D.getInDomain('playback.selectors.getCurrentItemPandoraId'),
      );
      const isPlayingRemovedItem = idRemoved === currentPandoraId;
      let isPlayingRemovedItemChild;
      if (typeRemoved === D.Constants.SourceTypes.PandoraTypes.ALBUM) {
        const tracks = yield select(D.getInDomain('catalog.utils.getTracksForAlbum'), idRemoved);
        isPlayingRemovedItemChild = tracks.includes(currentPandoraId);
      }

      if (isPlayingRemovedItem || isPlayingRemovedItemChild) {
        yield put(
          D.getInDomain('playback.actions.playCurrentItemForSource')({
            sourceId: currentSourceId,
          }),
        );
      }
    }
  }
}

export function* fetchPlaylistCurators(action: $TSFixMe): $TSFixMe {
  const annotations = get(action, ['response', 'annotations'], {});
  const playlists = pickBy(annotations, annotation => {
    return D.Utils.SourceType.isPlaylistPandoraId(get(annotation, 'pandoraId'));
  });
  const playlistPandoraIds = compact(Object.keys(playlists));

  if (playlistPandoraIds.length) {
    const requestParams = D.GraphQL.Queries.getPlaylistCurators({
      pandoraIds: playlistPandoraIds,
    });
    yield call(D.Services.GraphQL.graphQLRequest(requestParams));
  }
}

export function* onAddCollectionItem({ payload: { pandoraId, pandoraType } }: $TSFixMe): $TSFixMe {
  if (D.isStationType(pandoraType)) {
    return;
  }
  const annotations = yield select(D.getAnnotationsById, pandoraId);
  const isPodcastType =
    annotations &&
    [
      D.Constants.SourceTypes.PandoraTypes.PODCAST,
      D.Constants.SourceTypes.PandoraTypes.EPISODE,
    ].includes(annotations.get('type'));

  if (isPodcastType) {
    yield put(D.hidePodcastCollectionsCoachmark(false));
  }
  if (
    annotations &&
    (D.isUnavailable(annotations) || D.isRadioOnly(annotations) || D.isRetired(annotations))
  ) {
    let message = D.messages.cannotCollectSong;

    if (D.isRetired(annotations)) {
      message = D.messages.podcastUnavailable;
    } else if (pandoraType === D.PandoraTypes.ALBUM) {
      message = D.messages.cannotCollectAlbum;
    }

    yield put(D.openModalCoachmark({ bodyMessage: message }));
    return;
  }
  yield call(CollectionServices.addItem, {
    pandoraId,
  });
}

export function* onRemoveCollectionItem({
  payload: { pandoraType, pandoraId, redirectToMyMusicPlaylists },
}: $TSFixMe): $TSFixMe {
  if (!D.isStationType(pandoraType)) {
    yield call(CollectionServices.removeItem, {
      pandoraId,
      redirectToMyMusicPlaylists,
    });
  }
}

export function* onGetSortedItems({
  payload: { filter, sortOrder, offset, limit, annotationLimit },
}: $TSFixMe): $TSFixMe {
  const isRecentModifiedPlaylists = filter === CollectionSorts.RECENT_MODIFIED_PLAYLISTS;
  const collectionV7EndpointsEnabled = yield select(D.Utils.TunerVars.collectionV7EndpointsEnabled);
  const hasPremiumFeatures = yield select(D.Utils.Listener.hasPremiumFeatures);
  const params = {
    annotationLimit,
    collectionV7EndpointsEnabled,
    isRecentModifiedPlaylists,
    limit,
    offset,
    sortOrder,
  };

  if (!isFinite(offset)) {
    return;
  }

  if (filter !== FilterTypes.STATIONS) {
    yield put(setFetchingOffsets(offset, filter));
  }

  let typePrefixes;

  if (filter === FilterTypes.PLAYLISTS || isRecentModifiedPlaylists) {
    yield call(CollectionServices.getSortedPlaylists, {
      ...params,
      allowedTypes: D.Constants.SourceTypes.PlaylistSupportedTypes,
    });
  } else {
    switch (filter) {
      case FilterTypes.ALL:
        typePrefixes =
          collectionV7EndpointsEnabled && !hasPremiumFeatures
            ? GET_SORTED_BY_TYPES_ALL
            : GET_SORTED_BY_TYPES_ALL_PREMIUM;
        yield call(CollectionServices.getSortedByTypes, {
          ...params,
          typePrefixes,
        });
        break;
      case FilterTypes.ALBUMS:
        typePrefixes = [D.Constants.SourceTypes.PandoraTypes.ALBUM];
        yield call(CollectionServices.getSortedByTypes, {
          ...params,
          typePrefixes,
        });
        break;
      case FilterTypes.ARTISTS:
        typePrefixes = [D.Constants.SourceTypes.PandoraTypes.ARTIST];
        yield call(CollectionServices.getSortedByTypes, {
          ...params,
          typePrefixes,
        });
        break;
      case FilterTypes.SONGS:
        typePrefixes = [D.Constants.SourceTypes.PandoraTypes.SONG];
        yield call(CollectionServices.getSortedByTypes, {
          ...params,
          typePrefixes,
        });
        break;
      case FilterTypes.PODCASTS:
        typePrefixes = [D.Constants.SourceTypes.PandoraTypes.PODCAST];
        yield call(CollectionServices.getSortedByTypes, {
          ...params,
          typePrefixes,
        });
        break;
      case FilterTypes.EPISODES:
        typePrefixes = [D.Constants.SourceTypes.PandoraTypes.EPISODE];
        yield call(CollectionServices.getSortedByTypes, {
          ...params,
          typePrefixes,
        });
        break;
    }
  }
}

export function* onGetItemsByArtist({
  payload: { artistPandoraId, annotationLimit },
}: $TSFixMe): $TSFixMe {
  yield call(CollectionServices.getItemsByArtist, {
    artistPandoraId,
    annotationLimit,
  });
}

export function* onPlaylistAppendItemsSuccess(action: $TSFixMe): $TSFixMe {
  const pandoraIds = get(action, 'params.content.request.itemPandoraIds') || [];
  const playlistId = get(action, 'params.content.request.pandoraId');
  // @ts-expect-error TS(2348) FIXME: Value of type 'MapConstructor' is not callable. Di... Remove this comment to see the full error message
  const playlist = yield select(D.getAnnotationsById, playlistId) || Map();
  const playlistUrl = playlist.get('shareableUrlPath');
  const currentPath = yield select(D.getCurrentPathnameFromState);
  // We don't want to show the confirmation toast when the append action happened on the BSP
  // of the playlist added to OR if no items were actually added.
  if (pandoraIds.length === 0 || playlistUrl === currentPath) {
    return;
  }

  const m = D.messages;
  let message;
  let messageValues;

  if (pandoraIds.length === 1) {
    const pandoraId = pandoraIds[0];
    // @ts-expect-error TS(2348) FIXME: Value of type 'MapConstructor' is not callable. Di... Remove this comment to see the full error message
    const annotations = yield select(D.getAnnotationsById, pandoraId) || Map();
    const numTracks = annotations.get('trackCount') || annotations.get('totalTracks') || 1;
    messageValues = { playlistName: action.response.name, num: numTracks };
    message = <FormattedMessage {...m.songsAddedToPlaylist} values={messageValues} />;
  } else if (pandoraIds.length > 1) {
    messageValues = {
      playlistName: action.response.name,
      num: pandoraIds.length,
    };
    message = <FormattedMessage {...m.songsAddedToPlaylist} values={messageValues} />;
  }

  const { dispatch } = D.getStore();
  const modalConfig = {
    bodyMessage: message,
    buttonCallback: (): $TSFixMe => {
      dispatch(D.closeModal(undefined));
      dispatch(push(playlistUrl));
    },
    buttonMessage: <FormattedMessage {...m.viewPlaylist} />,
  };
  yield put(D.openModalCoachmarkWithButton(modalConfig));

  // Keep add to playlist submenu in most recent modified order
  // Invalidate fetched offsets for recent modified playlist
  // @ts-expect-error TS(2345) FIXME: Argument of type '"RECENT_MODIFIED_PLAYLISTS"' is ... Remove this comment to see the full error message
  yield put(resetFilterOffsets(CollectionSorts.RECENT_MODIFIED_PLAYLISTS));
  // If my music is currently on most recent modified playlists, we'll also invalidate fetched offsets
  // Note of confusion: most recent added is treated like most recent modified in my music for playlists
  const currentFilter = yield select(getCurrentFilter);
  const sort = yield select(getFilterSort, currentFilter);
  if (currentFilter === FilterTypes.PLAYLISTS && sort === CollectionSorts.MOST_RECENT_ADDED) {
    yield put(resetFilterOffsets(FilterTypes.PLAYLISTS));
  }

  // Update the first page, and let infinite scrollers update the rest
  yield put(
    D.getSortedCollectionItems({
      annotationLimit: ANNOTATION_LIMIT,
      filter: CollectionSorts.RECENT_MODIFIED_PLAYLISTS,
      limit: ANNOTATION_LIMIT,
      offset: 0,
      sortOrder: CollectionSorts.RECENT_MODIFIED,
    }),
  );
}

export function* onPlaylistUpdateSuccess(): $TSFixMe {
  // whenever a playlist is added, removed, or edited in any way, update the collection items to display the new state
  const previousVersion = yield select(getCollectionPreviousVersion);
  yield spawn(getItems, { sinceVersion: previousVersion });
}

export function* loadFilterAtOffset(filter: $TSFixMe, offset: $TSFixMe): $TSFixMe {
  const sort = yield select(getFilterSort, filter);
  yield put(
    D.getSortedCollectionItems({
      annotationLimit: SORTED_ITEMS_LIMIT,
      filter,
      limit: SORTED_ITEMS_LIMIT,
      offset,
      sortOrder: sort,
    }),
  );
}

export function* onLoadFilterAtCurrentOffset({ payload: { filter } }: $TSFixMe): $TSFixMe {
  const currentViewedOffset = yield select(getFilterCurrentViewedOffset, filter);
  yield call(loadFilterAtOffset, filter, currentViewedOffset);
}

export function* onCollectionUpdateSuccess(): $TSFixMe {
  // We need to reset the loaded pages for all filters after an update because we don't have
  // enough information to 1) make the updates uniformly in the correct places due to
  // random access lazy loading and 2) be sure to add/remove all the correct items in all
  // cases
  for (const filterType in FilterTypes) {
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (FilterTypes[filterType] !== FilterTypes.ARTISTS) {
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      yield put(resetFilterOffsets(FilterTypes[filterType]));
    }
  }
  // Also Invalidate Add to Playlist submenu offsets
  // @ts-expect-error TS(2345) FIXME: Argument of type '"RECENT_MODIFIED_PLAYLISTS"' is ... Remove this comment to see the full error message
  yield put(resetFilterOffsets(CollectionSorts.RECENT_MODIFIED_PLAYLISTS));
  const currentFilter = yield select(getCurrentFilter);
  const currentFilterCount = yield select(getFilteredCollectionTotalCount, currentFilter);
  // We want to auto-reload a page of data in the current filtered view only if we've loaded
  // the view before.
  if (isFinite(currentFilterCount)) {
    const currentViewedOffset = yield select(getFilterCurrentViewedOffset, currentFilter);
    yield spawn(loadFilterAtOffset, currentFilter, currentViewedOffset);
  }
}

export function* callGetCollectionVersion(): $TSFixMe {
  yield put(D.getVersion());
}

export function* onGetCollectionVersion(): $TSFixMe {
  const isCurrentlySyncing = yield select(getIsCurrentlySyncing);
  // only call getVersion if we're not currently in the middle of a sync
  if (!isCurrentlySyncing) {
    yield D.tryCall(CollectionServices.getVersion);
  }
}

export function* onGetCollectionVersionSuccess(action: $TSFixMe): $TSFixMe {
  const version = action.response;
  const previousVersion = yield select(getCollectionVersion);
  if (previousVersion !== version) {
    yield call(getItems, {
      sinceVersion: previousVersion ? previousVersion : null,
    });
  }
}

function* pollCollectionVersion(): $TSFixMe {
  // Poll the collection version every 30 seconds from the server
  const pollCollectionInterval = yield select(D.getPollCollectionInterval);
  while (LOOP) {
    yield delay(pollCollectionInterval);

    // If there's an expired PA reward, the call to getVersion will 401 and kick
    // the user out of PA. We only want that to happen at the end of a song
    // though.
    const hasExpiredActivePremiumAccess = yield select(
      D.getInDomain('rewards.utils.hasExpiredActivePremiumAccess', false),
    );
    if (!hasExpiredActivePremiumAccess) {
      yield call(onGetCollectionVersion);
    }
  }
}

export function* onStationCreated(action: $TSFixMe): $TSFixMe {
  const shuffleId = yield select(D.getInDomain('playback.utils.getShuffleStationId'));
  const stationResult = get(action, 'response.result', null);
  if (stationResult !== shuffleId) {
    const message = (
      <FormattedMessage
        {...D.messages.itemAddedToCollection}
        values={{ pandoraType: D.PandoraTypes.STATION }}
      />
    );
    yield put(D.openModalCoachmark({ bodyMessage: message }));
  }

  const seenWebPremiumWelcome = yield select(D.hasSeenWebPremiumWelcome);
  if (!seenWebPremiumWelcome) {
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ seenWebPremiumWelcome: true; }... Remove this comment to see the full error message
    yield put(D.ApiUpdateSettings({ seenWebPremiumWelcome: true }));
  }
}

export function* openPremiumWelcomeModal(): $TSFixMe {
  yield put(D.openModal({ modalType: 'ModalPremiumWelcome' }, null, D.PremiumWelcomeModalSubtype));
}

export function* countToRecentlyPlayedThreshold(): $TSFixMe {
  let counter = 0;
  let previousTime = 0;
  while (counter < RECENTLY_PLAYED_PLAYBACK_TIMER_THRESHOLD) {
    const updateAction = yield take(D.Constants.AudioPlayer.AudioPlayerActions.UPDATE);
    const currentTime = updateAction.payload[D.Constants.AudioPlayer.AudioPlayerKeys.CURRENT_TIME];
    const timeDifference = Math.abs(Math.floor(previousTime) - Math.floor(currentTime));
    const isPlaying = yield select(D.getInDomain('playback.utils.isAudioPlaying'));

    if (timeDifference >= 1) {
      previousTime = currentTime;
    }

    if (timeDifference === 1 && isPlaying) {
      counter += 1;
    }
  }
}

export function* onSPSTrackStart(action: $TSFixMe): $TSFixMe {
  const { id, type } = action.payload ?? action.data; // TODO-PAYLOAD: WEBCA-2505
  // We are still using recently played podcasts on the now playing page so we
  // still need to add this to state. Note that the RECENTLY_PLAYED_PODCASTS is it's own
  // separate key in the redux state, so it is unrelated to the RECENTLY_PLAYED_ITEMS state.
  if (type === D.Constants.SourceTypes.SourceTypes.PODCAST) {
    yield put(
      addRecentlyPlayedPodcasts({
        payload: {
          id,
          lastPlayed: Date.now(),
          // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          pandoraType: D.Constants.SourceTypes.PandoraTypes[type],
        },
      }),
    );
  }

  const spsRecentlyPlayedItems = yield select(getRecentlyPlayedSPSItems);
  const firstItem = spsRecentlyPlayedItems.first();
  if (id && id !== get(firstItem, 'id', null)) {
    yield call(countToRecentlyPlayedThreshold);
    yield put(setHasMetRecentlyPlayedThreshold(true));
  }
}

export function* onRemoveRecentlyPlayedItem(): $TSFixMe {
  yield delay(2000);
  yield put(setHasMetRecentlyPlayedThreshold(true));
}

export function* onVisibilityChange(): $TSFixMe {
  const currentPath = yield select(D.getCurrentPathnameFromState);
  const isCollectionPage = D.Utils.Route.isCollectionPath(currentPath);
  if (!isCollectionPage) return;

  if (D.Utils.PageVisibility.isHidden()) {
    yield put(setHasMetRecentlyPlayedThreshold(false));
  } else {
    yield put(setHasMetRecentlyPlayedThreshold(true));
  }
}
